import classnames from 'classnames';
import React, { forwardRef, Ref, useId } from 'react';

import { AttentionMessage, AttentionMessageTypes } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';

import Labels from './Labels';
import RadioButton from './RadioButton';
import { PanelButtonProps } from './Types';
import { toDataTrackIdFormat } from './utils/toDataTrackIdFormat';

const RadioButtonPanel = forwardRef(
    (
        {
            value,
            id,
            label,
            description,
            inputRef,
            selectedOption,
            onChange,
            name,
            describedBy,
            required,
            error,
            attentionMessage,
            labels,
            dataTrackId,
            dataTrackLinkname = value || label,
            grayed = false,
            locked = false,
            children,
            ...rest
        }: React.PropsWithChildren<PanelButtonProps>,
        ref: Ref<HTMLInputElement>,
    ): JSX.Element => {
        const generatedId = useId();
        const optionId = id ?? generatedId;
        const generatedAttentionMessageId = useId();
        const attentionMessageId = attentionMessage ? generatedAttentionMessageId : undefined;
        return (
            <>
                <label
                    className={classnames(
                        'sgw-radio-button-panel-option',
                        'sgw-radio-button-panel-option__panel-wrapper',
                        {
                            'sgw-radio-button-panel-option__checked': value === selectedOption,
                            'sgw-radio-button-panel-option__error': error,
                            'with-attention-message': !!attentionMessage,
                            'sgw-radio-button-panel-option__grayed': grayed || locked,
                            'sgw-radio-button-panel-option__locked': locked,
                            'sgw-radio-button-panel-option__attention-label': labels,
                            disabled: rest.disabled,
                        },
                    )}
                >
                    <Labels labels={labels} />
                    <RadioButton
                        {...rest}
                        className={classnames('sgw-radio-button-option__input', rest.className)}
                        selectedOption={selectedOption}
                        error={error}
                        aria-describedby={attentionMessageId ?? describedBy}
                        required={required}
                        aria-invalid={error}
                        onChange={onChange}
                        name={name}
                        id={optionId}
                        dataTrackId={toDataTrackIdFormat(dataTrackId)}
                        dataTrackLinkname={dataTrackId ? toDataTrackIdFormat(dataTrackLinkname) : undefined}
                        ref={ref ?? inputRef}
                        value={value}
                        label={label}
                    />
                    {description && <span className="sgw-radio-button-panel-option-description">{description}</span>}
                    {children && <div className="sgw-radio-button-panel-option-children">{children}</div>}
                </label>
                {attentionMessage && (
                    <AttentionMessage
                        id={attentionMessageId}
                        className="sgw-radio-button-panel__attention-message"
                        {...attentionMessage}
                        type={attentionMessage.type ?? AttentionMessageTypes.ATTENTION}
                        text={attentionMessage.text}
                        icon={attentionMessage.icon ?? IconTypes.UTROPSTEGN_24}
                    />
                )}
            </>
        );
    },
);
export default RadioButtonPanel;
